import { useRef } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Plus, Trash2, Eraser } from "lucide-react";

export const US_STATES = "AL AK AZ AR CA CO CT DE DC FL GA HI ID IL IN IA KS KY LA ME MD MA MI MN MS MO MT NE NV NH NJ NM NY NC ND OH OK OR PA RI SC SD TN TX UT VT VA WA WV WI WY".split(" ");

export function Field({ label, children, hint, testid }) {
  return <div data-testid={testid}><Label className="text-xs text-slate-600">{label}</Label>{children}{hint && <p className="text-[11px] text-slate-500 mt-0.5">{hint}</p>}</div>;
}

export function Text({ value, onChange, type = "text", ...rest }) {
  return <Input type={type} value={value ?? ""} onChange={(e) => onChange(e.target.value)} className="h-11 text-base" {...rest} />;
}

export function Toggle({ label, value, onChange, testid }) {
  return (
    <label className={`flex items-center gap-3 rounded-lg border px-3 py-2.5 cursor-pointer ${value ? "border-blue-500 bg-blue-50" : "border-slate-200 bg-white"}`}>
      <input type="checkbox" checked={!!value} onChange={(e) => onChange(e.target.checked)} className="h-5 w-5" data-testid={testid} /><span className="text-sm text-slate-800">{label}</span>
    </label>
  );
}

export function RepeatList({ items, onChange, fields, addLabel, emptyText, testid }) {
  const rows = items || [];
  const set = (i, k, v) => onChange(rows.map((r, j) => (j === i ? { ...r, [k]: v } : r)));
  return (
    <div className="space-y-3" data-testid={testid}>
      {!rows.length && <p className="text-sm text-slate-500 italic">{emptyText}</p>}
      {rows.map((r, i) => (
        <div key={i} className="rounded-lg border border-slate-200 bg-white p-3 space-y-2 relative" data-testid={`${testid}-row-${i}`}>
          <button type="button" onClick={() => onChange(rows.filter((_, j) => j !== i))} className="absolute top-2 right-2 text-slate-400 hover:text-red-600" data-testid={`${testid}-remove-${i}`}><Trash2 className="h-4 w-4" /></button>
          <div className="grid grid-cols-2 gap-2">
            {fields.map((f) => (
              <div key={f.key} className={f.wide ? "col-span-2" : ""}>
                <Label className="text-[11px] text-slate-500">{f.label}</Label>
                {f.type === "bool" ? <Toggle label={f.label} value={r[f.key]} onChange={(v) => set(i, f.key, v)} />
                  : f.type === "select" ? <select value={r[f.key] || ""} onChange={(e) => set(i, f.key, e.target.value)} className="w-full h-10 rounded-md border border-slate-200 bg-white px-2 text-sm"><option value="">—</option>{f.options.map((o) => <option key={o} value={o}>{o}</option>)}</select>
                  : <Text value={r[f.key]} onChange={(v) => set(i, f.key, v)} type={f.type || "text"} placeholder={f.placeholder} data-testid={`${testid}-${f.key}-${i}`} />}
              </div>
            ))}
          </div>
        </div>
      ))}
      <Button type="button" variant="outline" className="w-full h-11" onClick={() => onChange([...rows, {}])} data-testid={`${testid}-add`}><Plus className="h-4 w-4 mr-1" />{addLabel}</Button>
    </div>
  );
}

export function SignaturePad({ onChange }) {
  const ref = useRef(null); const drawing = useRef(false); const dirty = useRef(false);
  const pos = (e) => { const r = ref.current.getBoundingClientRect(); const p = e.touches ? e.touches[0] : e; return { x: (p.clientX - r.left) * (ref.current.width / r.width), y: (p.clientY - r.top) * (ref.current.height / r.height) }; };
  const start = (e) => { e.preventDefault(); drawing.current = true; const c = ref.current.getContext("2d"); const { x, y } = pos(e); c.beginPath(); c.moveTo(x, y); };
  const move = (e) => { if (!drawing.current) return; e.preventDefault(); const c = ref.current.getContext("2d"); c.lineWidth = 2.5; c.lineCap = "round"; c.strokeStyle = "#0f172a"; const { x, y } = pos(e); c.lineTo(x, y); c.stroke(); dirty.current = true; };
  const end = () => { if (!drawing.current) return; drawing.current = false; if (dirty.current) onChange(ref.current.toDataURL("image/png")); };
  const clear = () => { const c = ref.current.getContext("2d"); c.clearRect(0, 0, ref.current.width, ref.current.height); dirty.current = false; onChange(null); };
  return (
    <div>
      <canvas ref={ref} width={700} height={220} className="w-full h-44 rounded-md border-2 border-dashed border-slate-300 bg-white touch-none" data-testid="apply-sign-pad" onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end} onTouchStart={start} onTouchMove={move} onTouchEnd={end} />
      <div className="flex justify-between items-center mt-1"><span className="text-[11px] text-slate-500">Sign with your finger or mouse</span><Button type="button" size="sm" variant="ghost" onClick={clear} data-testid="apply-sign-clear"><Eraser className="h-3.5 w-3.5 mr-1" />Clear</Button></div>
    </div>
  );
}
